<template>
  <div class="rd-meal-panel">
    <div v-if="!detail" class="rd-empty rd-empty--tight">
      <b>{{ $t("report_mobile.no_meals") }}</b>
    </div>

    <template v-else>
      <div class="rd-card">
        <table class="rd-tbl">
          <thead>
            <tr>
              <th scope="col">{{ $t("firm") }}</th>
              <th v-for="shift in detail.shifts" :key="shift.id" scope="col" class="is-right">
                {{ $t("report_mobile.shift_short", { n: shift.id }) }}
              </th>
              <th scope="col" class="is-right">{{ $t("total") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="company in detail.companies" :key="company.name">
              <td class="is-name" :title="company.name">{{ company.name }}</td>
              <td
                v-for="shift in detail.shifts"
                :key="shift.id"
                class="is-right rd-num"
                :class="{ 'is-zero': !company[shift.key] }"
              >
                {{ company[shift.key] }}
              </td>
              <td class="is-right rd-num"><b>{{ company.total }}</b></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="rd-section">
        <h2>{{ $t("report_desktop.who_ordered") }}</h2>
        <span class="rd-num">{{ workerCount }}</span>
      </div>

      <div v-if="loadingWorkers" class="rd-skeleton" style="height: 120px"></div>
      <p v-else-if="failedWorkers" class="rd-muted">{{ $t("report_mobile.load_failed") }}</p>
      <template v-else>
        <section v-for="group in workerGroups" :key="group.name" class="rd-order-card">
          <div class="rd-order-card__head">
            <b>{{ group.name }}</b>
            <span class="rd-pill">{{ group.people.length }}</span>
          </div>
          <ul>
            <li v-for="person in group.people" :key="person.key">
              <span class="rd-avatar rd-avatar--sm">{{ person.initials }}</span>
              <span class="rd-order-card__name">{{ person.name }}</span>
              <span v-if="person.quantity > 1" class="rd-muted rd-num">{{ person.quantity }}×</span>
            </li>
          </ul>
        </section>
        <p v-if="!workerGroups.length" class="rd-muted">{{ $t("report_desktop.no_workers_found") }}</p>
      </template>

      <button type="button" class="rd-btn" @click="$emit('open-screen', 'workerOrders', { search: detail.name })">
        <v-icon size="16">mdi-table</v-icon>
        {{ $t("report_mobile.worker_orders") }}
      </button>
    </template>
  </div>
</template>

<script>
import reportService from "@/services/report";

/**
 * Panel "Obrok": razlaganje po firmama (iz izveštaja Obroci) i ko je naručio taj obrok
 * (poziv userlist za firme koje ga imaju). Podaci o obroku su živi: čitaju se iz ekrana Obroci.
 */
export default {
  name: "ReportDesktopMealPanel",
  props: {
    /** Ekran Obroci koji drži podatke */
    owner: {
      type: Object,
      required: true,
    },
    mealId: {
      type: [Number, String],
      required: true,
    },
  },
  data: () => ({
    loadingWorkers: false,
    failedWorkers: false,
    users: {},
  }),
  computed: {
    detail() {
      return this.owner.mealDetail(this.mealId);
    },
    mealCompanies() {
      return this.$store.state.report.mealCompanies || [];
    },
    workerGroups() {
      if (!this.detail) {
        return [];
      }
      return this.detail.companies
        .map((company) => ({ name: company.name, people: this.users[company.name] || [] }))
        .filter((group) => group.people.length);
    },
    workerCount() {
      return this.workerGroups.reduce((sum, group) => sum + group.people.length, 0) || "";
    },
  },
  created() {
    this.loadWorkers();
  },
  methods: {
    realCompany(name) {
      return this.mealCompanies.find((company) => this.toLatin(company.name) === name) || null;
    },
    async loadWorkers() {
      if (!this.detail) {
        return;
      }
      const companies = this.detail.companies
        .map((company) => ({ name: company.name, real: this.realCompany(company.name) }))
        .filter((company) => company.real);
      if (!companies.length) {
        return;
      }
      this.loadingWorkers = true;
      this.failedWorkers = false;
      const { from, to } = this.$store.state.report.filters.dates;
      const ids = companies.map((company) => company.real.id);
      try {
        const { data } = await reportService.getCompanyUserList(ids.join(","), { date: from, date2: to });
        // Jedna firma: odgovor je direktno { meals, users }, više firmi: { [id]: { meals, users } }
        const byId = Object.keys(data || {}).includes("meals") ? { [ids[0]]: data } : data || {};
        const users = {};
        companies.forEach((company) => {
          users[company.name] = this.peopleFor(byId[company.real.id]);
        });
        this.users = users;
      } catch (error) {
        this.failedWorkers = true;
      } finally {
        this.loadingWorkers = false;
      }
    },
    /** Radnici koji su naručili ovaj obrok u izabranim smenama. */
    peopleFor(companyData) {
      const meals = companyData?.meals || [];
      const wantedName = this.detail.name;
      const meal =
        meals.find((item) => item.id === this.mealId) ||
        meals.find((item) => item.name === wantedName);
      if (!meal) {
        return [];
      }
      const people = [];
      this.owner.selectedShifts.forEach((shiftId) => {
        const users = meal.users?.[`${shiftId}-smena`];
        (Array.isArray(users) ? users : []).forEach((user) => {
          const name = [user.name, user.lastname].filter(Boolean).map(this.toLatin).join(" ");
          people.push({
            key: `${shiftId}-${user.id}`,
            name,
            initials: [user.name, user.lastname]
              .filter(Boolean)
              .map((part) => this.toLatin(part).charAt(0))
              .join(""),
            quantity: user.quantity || 1,
          });
        });
      });
      return people.sort((a, b) => a.name.localeCompare(b.name));
    },
  },
};
</script>
